import {
    MarkerClusterer,
    SuperClusterAlgorithm
} from '@googlemaps/markerclusterer';

import PropTypes from 'prop-types';

import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';

import {
    IconMinus,
    IconPlus
} from '$partials/icons';

import { loadScript } from '$utilities/requests';

/* ========================================================================= */
/* Style objects */
/* ========================================================================= */

const mapStyles = [
    {
        'featureType': 'administrative',
        'elementType': 'labels.text.fill',
        'stylers': [
            {
                'color': '#444444'
            }
        ]
    },
    {
        'featureType': 'administrative.country',
        'elementType': 'labels',
        'stylers': [
            {
                'visibility': 'off'
            }
        ]
    },
    {
        'featureType': 'administrative.province',
        'elementType': 'labels',
        'stylers': [
            {
                'visibility': 'off'
            }
        ]
    },
    {
        'featureType': 'administrative.locality',
        'elementType': 'labels',
        'stylers': [
            {
                'visibility': 'off'
            }
        ]
    },
    {
        'featureType': 'administrative.neighborhood',
        'elementType': 'labels',
        'stylers': [
            {
                'visibility': 'off'
            }
        ]
    },
    {
        'featureType': 'landscape',
        'elementType': 'all',
        'stylers': [
            {
                'color': '#879E71'
            }
        ]
    },
    {
        'featureType': 'poi',
        'elementType': 'all',
        'stylers': [
            {
                'visibility': 'off'
            }
        ]
    },
    {
        'featureType': 'road',
        'elementType': 'all',
        'stylers': [
            {
                'visibility': 'off'
            },
            {
                'saturation': -100
            },
            {
                'lightness': 45
            }
        ]
    },
    {
        'featureType': 'road.highway',
        'stylers': [
            {
                'visibility': 'off'
            }
        ]
    },
    {
        'featureType': 'road',
        'elementType': 'labels',
        'stylers': [
            {
                'visibility': 'off'
            }
        ]
    },
    {
        'featureType': 'road.highway',
        'elementType': 'labels',
        'stylers': [
            {
                'visibility': 'off'
            }
        ]
    },
    {
        'featureType': 'road.arterial',
        'elementType': 'labels.icon',
        'stylers': [
            {
                'visibility': 'off'
            }
        ]
    },
    {
        'featureType': 'road.local',
        'elementType': 'labels',
        'stylers': [
            {
                'visibility': 'off'
            }
        ]
    },
    {
        'featureType': 'transit',
        'elementType': 'geometry.fill',
        'stylers': [
            {
                'visibility': 'off'
            },
            {
                'saturation': -100
            },
            {
                'lightness': 45
            }
        ]
    },
    {
        'featureType': 'transit',
        'elementType': 'geometry.stroke',
        'stylers': [
            {
                'visibility': 'off'
            }
        ]
    },
    {
        'featureType': 'transit',
        'elementType': 'labels.icon',
        'stylers': [
            {
                'visibility': 'off'
            }
        ]
    },
    {
        'featureType': 'transit',
        'elementType': 'labels.text',
        'stylers': [
            {
                'visibility': 'off'
            }
        ]
    },
    {
        'featureType': 'water',
        'elementType': 'all',
        'stylers': [
            {
                'color': '#F2EDE1'
            },
            {
                'visibility': 'on'
            }
        ]
    }
];

/* ========================================================================= */
/* Shared variables */
/* ========================================================================= */

const sharedMemory = {
    isRequested: false,
    isLoaded: false,
    queue: []
};

const londonOfficeLatLng = {
    latitude: 51.52416513154871,
    longitude: -0.10898654602363306
};

/* ========================================================================= */
/* Context(s) */
/* ========================================================================= */

const Context = createContext();

/* ========================================================================= */
/* Functions & Hooks */
/* ========================================================================= */

/**
 * @function createMap
 * @param {object} mapRef - The reference to the map container.
 *
 * Creates a Google Map instance
 */
const createMap = (mapRef) => {
    return new window.google.maps.Map(
        mapRef.current,
        {
            fullscreenControl: false,
            mapTypeControl: false,
            mapTypeId: window.google.maps.MapTypeId.ROADMAP,
            maxZoom: 9,
            minZoom: 5,
            scaleControl: false,
            scrollwheel: false,
            scrollWheelZoom:'center',
            streetViewControl: false,
            styles: mapStyles,
            zoom: 6,
            zoomControl: false
        }
    );
};


/**
 * @function getMarkerLabel
 * @param {object} marker
 * @param {string} markerActiveId
 *
 * Returns a relevant label object for marker styling
 */
const getMarkerLabel = (marker, markerActiveId) => {
    let label;

    if (marker.name) {
        if (marker.id === markerActiveId) {
            label = {
                text: marker.name,
                className: 'map__marker map__marker--region-active'
            };
        } else {
            label = {
                text: marker.name,
                className: 'map__marker map__marker--region'
            };
        }
    } else {
        if (marker.id === markerActiveId) {
            label = {
                text: ' ',
                className: 'map__marker map__marker--point-active'
            };
        } else {
            label = {
                text: ' ',
                className: 'map__marker map__marker--point'
            };
        }
    }

    return label;
};


/**
 * @function drawMarkers
 * @param {object} googleMapInstance
 * @param {array} locations
 * @param {object} markerActive
 * @param {function} setMarkerActive
 * @param {array} markers
 * @param {function} setMarkers
 *
 * Creates a set of markers and zooms the map to fit them in view
 */
const drawMarkers = (
    googleMapInstance = null,
    locations = null,
    markerActive = null,
    setMarkerActive = null
) => {
    // exit if no locations specified
    if (!googleMapInstance || !locations || locations?.length === 0 || !window.google?.maps?.Marker) {
        return;
    }

    // add each location to map as marker
    const markers = locations.map((location) => {
        // return if not a valid location
        if (!location?.id || !location?.lat || !location?.lng) {
            return null;
        }

        // create google marker from location item
        const marker = new window.google.maps.Marker({
            icon: null,
            label: getMarkerLabel(location, markerActive?.id),
            map: googleMapInstance,
            position: {
                lat: parseFloat(location.lat),
                lng: parseFloat(location.lng)
            }
        });
        marker.id = location.id;
        marker.name = location?.name;

        // calulcate distance from marker to London/Farringdon office (used for sorting in map view)
        if (window.google.maps?.geometry) {
            const startLatLng = new window.google.maps.LatLng(londonOfficeLatLng.latitude, londonOfficeLatLng.longitude);
            marker.distanceToLondonOffice = window.google.maps.geometry.spherical.computeDistanceBetween(startLatLng, marker.position);
        }

        // assign on click handler to marker
        marker.addListener('click', () => {
            setMarkerActive(marker);
        });

        return marker;
    })
        .filter(Boolean)
        .sort((a, b) => a.distanceToLondonOffice - b.distanceToLondonOffice);

    // create google marker cluster from markers
    const cluster = new MarkerClusterer({
        algorithm: new SuperClusterAlgorithm({ radius: 40 }),
        map: googleMapInstance,
        markers: markers,
        onClusterClick: (e, cluster) => {
            setMarkerActive(cluster?.markers?.[0]);
        },
        renderer: {
            render: ({ count, position }) => {
                return new window.google.maps.Marker({
                    icon: { url: ' ' },
                    label: {
                        className: 'map__marker map__marker--cluster',
                        text: String(count)
                    },
                    position: position
                });
            }
        }
    });

    // calculate bounds of all markers
    const bounds = new window.google.maps.LatLngBounds();
    markers.forEach((marker) => {
        bounds.extend({
            lat: marker.position.lat(),
            lng: marker.position.lng()
        });
    });

    // store reference to previous and next marker for use later
    markers.forEach((marker, idx) => {
        marker.next = markers?.[idx + 1] || markers?.[0];
        marker.previous = markers?.[idx - 1] || markers?.[markers.length - 1];
    });

    // pan / zoom map to fit bounds in viewport
    googleMapInstance.fitBounds(bounds, 338);

    return {
        cluster,
        markers
    };
};


/**
 * @function updateMarkers
 * @param {object} googleMapInstance
 * @param {object} markerActive
 * @param {array} markers
 *
 * Update icons for markers already on map
 */
const updateMarkers = (googleMapInstance, markerActive, markers) => {
    if (!googleMapInstance) {
        return;
    }

    markers?.forEach((marker) => {
        marker.setLabel(getMarkerLabel(marker, markerActive?.id));
        marker.setIcon({ url: ' ' });

        if (marker.id === markerActive?.id) {
            googleMapInstance.panTo({
                lat: markerActive.position.lat(),
                lng: markerActive.position.lng()
            });
        }
    });
};


/**
 * @hook useGoogleMap
 * @param {object} mapRef - The reference to the map container
 * @param {object} extras
 *     @prop {boolean} isClusterable
 *     @prop {string} key - The API key provided by Google.
 *     @prop {array} locations
 *     @prop {string} markerActive
 *     @prop {function} setMarkerActive
 * @return {object|null} - The Google map instance
 *
 * 1. Initialises Google Maps when the component is mounted in the browser.
 * 1.1. If the Google Maps scripts are not loaded yet, let’s load it and execute the queue once initialised.
 * 1.2. Otherwise, let’s initialise the Google Map instance on load.
 */
const useGoogleMap = (mapRef, extras) => {
    const [ cluster, setCluster ] = useState(null);
    const [ googleMap, setGoogleMap ] = useState(null);
    const [ markers, setMarkers ] = useState(null);

    const init = () => {
        const googleMapInstance = createMap(mapRef);
        setGoogleMap(googleMapInstance);
    };

    /* [1] */
    useEffect(() => {
        if (!sharedMemory.isRequested && !window.google?.maps && extras.key) {
            sharedMemory.isRequested = true;
            sharedMemory.queue.push(init);

            /* [1.1] */
            loadScript('https://maps.googleapis.com/maps/api/js?key=' + extras.key + '&libraries=geometry', () => {
                sharedMemory.isLoaded = true;
                sharedMemory.queue.forEach(callback => callback());
            });
        } else if (!googleMap && mapRef.current) {
            /* [1.2] */
            sharedMemory.isLoaded ? init() : sharedMemory.queue.push(init);
        }
    }, []);

    useEffect(() => {
        // remove old clusters from map
        if (cluster) {
            cluster.clearMarkers();
        }

        // remove old markers from map
        if (markers) {
            markers.forEach(mark => {
                mark.setMap(null);
            });
        }

        // draw new markers (& clusters) on map
        const dm = drawMarkers(
            googleMap,
            extras.locations,
            extras.markerActive,
            extras.setMarkerActive
        );

        if (dm) {
            // store new cluster in state
            setCluster(dm.cluster);

            // store new markers in state
            setMarkers(dm.markers);
        }
    }, [
        googleMap,
        extras.locations
    ]);

    useEffect(() => {
        updateMarkers(googleMap, extras.markerActive, markers);
    }, [
        extras.markerActive,
        googleMap,
        markers
    ]);

    return googleMap;
};

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @component Map
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const Map = ({
    locations = null,
    markerActive = null,
    setMarkerActive = null,
    ...remainingProps
}) => {
    // references
    const mapRef = useRef(null);

    // hooks
    const googleMap = useGoogleMap(
        mapRef,
        {
            markerActive: markerActive,
            key: 'AIzaSyAUztKucLJNmw27A_AX0xt5DpQZyaEsrKY',
            locations: locations,
            setMarkerActive: setMarkerActive
        }
    );

    const context = useMemo(() => ({ googleMap, mapRef }), [ googleMap, mapRef ]);

    // render
    return (
        <Context.Provider value={context}>
            <div
                className='map'
                { ...remainingProps } />
        </Context.Provider>
    );
};


/**
 * @component MapCanvas
 */
const MapCanvas = () => {
    // references
    const { mapRef } = useContext(Context);

    // render
    return (
        <div
            className='map__canvas'
            ref={mapRef} />
    );
};


/**
 * @component MapControls
 */
const MapControls = () => {
    // references
    const { googleMap } = useContext(Context);

    // render
    return (
        <div className='map__zoom-wrapper'>
            <button
                className='map__zoom-in-button'
                onClick={() => googleMap.setZoom(googleMap.getZoom() + 1)}
                type='button'>
                <span className='sr-only'>
                    Zoom In
                </span>

                <IconPlus />
            </button>

            <button
                className='map__zoom-out-button'
                onClick={() => googleMap.setZoom(googleMap.getZoom() - 1)}
                type='button'>
                <span className='sr-only'>
                    Zoom Out
                </span>

                <IconMinus />
            </button>
        </div>
    );
};


/* ========================================================================= */
/* Typing & exports */
/* ========================================================================= */

Map.propTypes = {
    locations: PropTypes.array.isRequired,
    markerActive: PropTypes.object,
    setMarkerActive: PropTypes.func.isRequired
};


MapCanvas.propTypes = {};
MapControls.propTypes = {};


export {
    Map,
    MapCanvas,
    MapControls
};
